import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import AddAffiliatePopup from './AddAffiliatePopup';
import LoaderSpinner from '../common/Loader';
import AlertDialogModal from '../common/AlertDialog';
import SearchProduct from '../Inventory/Product/searchProduct';
import { isCustomRole } from '../../constants/roleConstants';
import {
    useAffiliateList,
    useAffiliateActions,
    useSortAndFilter,
} from './Hooks/AffiliateManagement.hooks';
import {
    AffiliatePageHeader,
    AffiliateTable,
    AggregateAffiliateTable,
    AffiliatePaginationSection,
    CompanySellerMultiSelectFilter,
} from './Subcomponents/AffiliateManagement.subcomponents';
import InvitationReferralsModal from './Subcomponents/InvitationReferralsModal';

// An aggregate (super-admin) row is signalled by ReferralCount != null,
// which per the API contract is equivalent to InviteToken === null.
const isAggregateRow = (row) => row?.ReferralCount !== null && row?.ReferralCount !== undefined;

export default function AffiliateManagement() {
    const [sortColumn, setSortColumn] = useState('');
    const [sortOrder, setSortOrder] = useState('DESCENDING');
    const [showAddAffiliatePopup, setShowAddAffiliatePopup] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedAffiliateForDelete, setSelectedAffiliateForDelete] = useState(null);
    const [referralModal, setReferralModal] = useState({ open: false, companySeller: null });
    const [showFilter, setShowFilter] = useState(false);

    const userDetails = useSelector((state) => state?.auth?.user);
    const isSuperAdmin = Boolean(userDetails?.Value?.isSuperAdmin);
    const currentUserIsCustomRole = isCustomRole(userDetails?.Value);

    const affiliateList = useAffiliateList();
    const actions = useAffiliateActions({
        setShowAddAffiliatePopup,
        setCurrentPageIndex: affiliateList.setCurrentPageIndex,
        setRefreshKey: affiliateList.setRefreshKey,
    });

    // Detect whether current page is aggregate (super-admin) or per-invitation
    const showAggregateView = useMemo(
        () => isSuperAdmin && affiliateList.affiliateData.length > 0 && affiliateList.affiliateData.every(isAggregateRow),
        [isSuperAdmin, affiliateList.affiliateData]
    );

    const sorting = useSortAndFilter(
        affiliateList.affiliateData,
        sortColumn,
        sortOrder,
        setSortColumn,
        setSortOrder
    );

    // Delete modal handlers (per-invitation view)
    const handleDeleteClick = (inviteId) => {
        setSelectedAffiliateForDelete(inviteId);
        setShowDeleteModal(true);
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setSelectedAffiliateForDelete(null);
    };

    const handleConfirmDelete = () => {
        actions.handleDeleteInvitation(selectedAffiliateForDelete);
        setShowDeleteModal(false);
        setSelectedAffiliateForDelete(null);
    };

    const handleReferralCountClick = (row) => {
        setReferralModal({
            open: true,
            companySeller: {
                CompanySellerId: row.CompanySellerId,
                CompanySellerName: row.CompanySellerName,
            },
        });
    };

    const handleReferralModalClose = () => {
        setReferralModal({ open: false, companySeller: null });
    };

    const triggerOuterRefresh = () => {
        affiliateList.setRefreshKey(prev => prev + 1);
    };

    // Typing debounces (in the hook); Search button flushes immediately; Reset clears.
    const handleSearchFilter = (_e, type) => {
        if (type === 'clear') {
            affiliateList.flushSearch('');
        } else {
            affiliateList.flushSearch();
        }
    };

    const startIdx = 0;

    return (
        <main>
            <div className="main__container">
                <AffiliatePageHeader
                    onAddClick={() => setShowAddAffiliatePopup(true)}
                    showAddButton={!currentUserIsCustomRole}
                    showFilterButton={isSuperAdmin}
                    onFilterClick={() => setShowFilter(v => !v)}
                />

                <SearchProduct
                    showSearch={true}
                    searchTxt={affiliateList.searchKeywords}
                    setSearchTxt={affiliateList.setSearchKeywords}
                    handleReset={handleSearchFilter}
                    handleSearchFilter={handleSearchFilter}
                    placeHolderText="Search by Company, Seller, Seller Code, STE Code, or Email"
                />

                {isSuperAdmin && showFilter && (
                    <CompanySellerMultiSelectFilter
                        showFilter={showFilter}
                        initialCompanies={affiliateList.companyFilter}
                        initialSellers={affiliateList.sellerFilter}
                        onApply={({ companies, sellers }) => {
                            affiliateList.setCompanyFilter(companies || []);
                            affiliateList.setSellerFilter(sellers || []);
                            affiliateList.setCurrentPageIndex(1);
                        }}
                        onClear={() => {
                            affiliateList.setCompanyFilter([]);
                            affiliateList.setSellerFilter([]);
                            affiliateList.setCurrentPageIndex(1);
                        }}
                    />
                )}

                <div className="main_content cus-page-content-sec incomingTbl">
                    <div className="top_right_button_container cus-content-only-sec">
                        <div className="incomingTbl tableFixHead main_content_table_history">
                            {affiliateList.loading && <LoaderSpinner />}
                            {!affiliateList.loading && showAggregateView && (
                                <AggregateAffiliateTable
                                    data={sorting.processedData}
                                    sortColumn={sorting.sortColumn}
                                    sortOrder={sorting.sortOrder}
                                    onSort={sorting.handleSortingColumn}
                                    startIdx={startIdx}
                                    onReferralCountClick={handleReferralCountClick}
                                />
                            )}
                            {!affiliateList.loading && !showAggregateView && (
                                <AffiliateTable
                                    data={sorting.processedData}
                                    sortColumn={sorting.sortColumn}
                                    sortOrder={sorting.sortOrder}
                                    onSort={sorting.handleSortingColumn}
                                    startIdx={startIdx}
                                    onDelete={handleDeleteClick}
                                    onCopy={actions.handleCopyInvitationUrl}
                                />
                            )}
                        </div>
                        <AlertDialogModal
                            modalClass={'modalDeleteWrapper'}
                            modalShow={showDeleteModal}
                            headingText={'Delete Invitation'}
                            messageText={'Are you sure you want to delete this invitation?'}
                            handleCancelDeleteAlert={handleCancelDelete}
                            handleYesDeleteAlert={handleConfirmDelete}
                        />
                    </div>
                </div>

                {!affiliateList.loading && (
                    <AffiliatePaginationSection
                        paginatedData={sorting.processedData}
                        pagination={{
                            numberOfPage: affiliateList.pageSize,
                            currentPage: affiliateList.currentPageIndex,
                            totalPages: Math.ceil(affiliateList.totalRecords / affiliateList.pageSize),
                            setCurrentPage: affiliateList.setCurrentPageIndex,
                            setNumberOfPage: affiliateList.setPageSize,
                        }}
                        affiliateDataLength={affiliateList.totalRecords}
                    />
                )}
            </div>

            <AddAffiliatePopup
                showAddAffiliatePopup={showAddAffiliatePopup}
                setShowAddAffiliatePopup={setShowAddAffiliatePopup}
                onAddAffiliate={actions.handleAddAffiliate}
            />

            {referralModal.open && (
                <InvitationReferralsModal
                    show={referralModal.open}
                    onHide={handleReferralModalClose}
                    companySeller={referralModal.companySeller}
                    onOuterRefresh={triggerOuterRefresh}
                />
            )}
        </main>
    );
}
